<template>
  <section class="lucky-dip-rail" :style="railStyle">
    <GlyphView :rail="rail" />
    <div class="title-container">
      <h3 class="rail-title" v-if="title">{{ title }}</h3>
      <p class="rail-subtitle" v-if="subtitle">{{ subtitle }}</p>
    </div>

    <div :id="containerId" class="lucky-dip-row">
      <LuckyDipCell
        v-for="(item, index) of items"
        :key="`${item.imageUrl}-${index}`"
        :item="item"
        :loading="loadingIndex === index"
        :id="itemId(index)"
        :tabindex="index"
        v-focus
        @up="leaveUp"
        @down="leaveDown"
        @left="moveLeft(index)"
        @right="moveRight(index)"
        @select="pickLuckyDip(index)"
      />
    </div>
  </section>
</template>

<script setup lang="ts">

import { ref, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';
import { navigationService } from 'vue-spatialnavigation';
import { RailResponse } from '@/types/cms';
import { Language } from '@/enums/language';
import { useStore } from '@/composables/useStore';
import { useRailNavigation } from '@/composables/useRailNavigation';
import { useCMSItemNavigation } from '@/composables/useCMSItemNavigation';
import { focusElementById } from '@/helpers/focus';
import api from '@/api';
import LuckyDipCell from '@/components/rails/LuckyDipCell.vue';
import GlyphView  from '@/components/glyphs/GlyphView.vue';

const props = defineProps<{
  rail: RailResponse;
  railIndex: number;
  railDidNavigateDown?: (railIndex: number) => void;
}>();

const { containerId, itemId, handleUp, handleDown, handleLeftFallback } =
  useRailNavigation(props);
const { openItem } = useCMSItemNavigation();

const instance = getCurrentInstance();
const lazyLoad = instance?.proxy?.$lazyLoad;

const { useGetter } = useStore();
const currentLanguage = useGetter<string>('currentLanguage');

const localised = (en: string | null, ga: string | null) => {
  const value = currentLanguage.value === Language.Irish ? ga : en;
  return value ?? en ?? '';
};

const title = computed(() => localised(props.rail.titleEn, props.rail.titleGa));
const subtitle = computed(() => localised(props.rail.subtitleEn, props.rail.subtitleGa));

const items = computed(() => props.rail.luckyDipItems ?? []);

const railStyle = computed(() => ({
  'background-color': props.rail.backgroundColor ?? undefined,
  color: props.rail.textColor ?? undefined,
}));


const loadingIndex = ref<number | null>(null);

const pickToken = ref(0);

const cancelPick = () => {
  pickToken.value += 1;
  loadingIndex.value = null;
};

const pickLuckyDip = async (index: number) => {
  if (loadingIndex.value !== null) return;

  const token = pickToken.value;
  loadingIndex.value = index;
  try {
    const item = await api.CMS.getLuckyDip();
    // Still the user's pending press, and this rail is still on screen.
    if (token === pickToken.value) openItem(item);
  } catch (error) {
    // Nothing to show the user on a remote: the tile simply comes back.
    console.warn('[rails] Lucky dip pick failed', error);
  } finally {
    if (token === pickToken.value) loadingIndex.value = null;
  }
};

const leaveUp = () => {
  cancelPick();
  handleUp();
};

const leaveDown = () => {
  cancelPick();
  handleDown();
};

onBeforeUnmount(cancelPick);

const focusItem = (index: number) => {
  const targetId = itemId(index);
  if (!navigationService.getFocusElementById(targetId)) return;

  const container = document.getElementById(containerId.value);
  const target = document.getElementById(targetId);
  if (container && target) {
    container.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
  }

  //Call helper
  focusElementById(targetId);
};

const moveLeft = (index: number) => {
  if (index === 0) {
    cancelPick();
    handleLeftFallback();
    return;
  }
  focusItem(index - 1);
};

const moveRight = (index: number) => {
  if (index < items.value.length - 1) focusItem(index + 1);
};

const scanForLazyImages = () => {
  setTimeout(() => {
    const container = document.getElementById(containerId.value);
    if (container) lazyLoad?.(container);
  }, 200);
};

onMounted(scanForLazyImages);


</script>

<style scoped lang="scss">
.lucky-dip-rail {
  position: relative;
  z-index: 0;
  padding-top: 5px;
  padding-bottom: 40px;
  padding-left: $rail-padding-left;
}

.title-container {
  text-align: left;
}

.rail-title {
  @include text-rail-title;
}

.rail-subtitle {
  @include text-rail-subtitle;
}

.lucky-dip-row {
  position: relative;
  display: flex;
  overflow-x: auto;
  padding-top: 20px;
}

.lucky-dip-row::-webkit-scrollbar {
  display: none;
}

.lucky-dip-row > * + * {
  margin-left: $cell-spacing;
}
</style>
